import { apiFetch, apiUrl, isSuccess } from "./apiClient";
import AppSettings from "../appsettings";

// Helper functions for handling image values which can be in different formats
// New image format supports object with derived versions (e.g. webp),
// but we want to maintain compatibility with older formats which are just strings.
export function getImageFilename(imageValue) {
  if (!imageValue) {
    return "";
  }

  if (typeof imageValue === "string") {
    return imageValue;
  }

  return (
    imageValue?.original?.filename ||
    imageValue?.filename ||
    imageValue?.derived?.webp?.filename ||
    ""
  );
}

// A ref points at the shared, content-addressed asset library (dedup model)
// when it carries the `assets/` prefix. Such assets live ONCE at the page root,
// shared across every version; legacy assets live inside a per-version folder.
const isSharedAssetRef = (filename = "") => filename.startsWith("assets/");

export function getDynamicAssetUrl(category, name, version, imageValue) {
  const filename = getImageFilename(imageValue);
  if (!filename) {
    return "";
  }
  if (/^https?:\/\//i.test(filename)) {
    return filename;
  }
  const base = AppSettings.cloudFrontURL?.replace(/(?<!\/)\/+$/, "") || "";
  if (isSharedAssetRef(filename)) {
    return `${base}/static/content/${category}/${name}/${filename}`;
  }
  return `${base}/static/content/${category}/${name}/${version}/${filename}`;
}

export function getDynamicContentPrefix(category, versionName) {
  const versionPath = String(versionName).replace("#", "/");
  return `/static/content/${category}/${versionPath}/`;
}

/**
 * Fetches the active (published) dynamic page for a category/name from the
 * unified dynamic-pages endpoint. Rejects on auth and network errors so callers
 * can distinguish "not found" (null) from "request failed".
 */
export async function getActiveDynamicPage(category, name) {
  const params = new URLSearchParams({ category, name, active: true });
  const response = await apiFetch(apiUrl(`/dynamicPages?${params}`));

  if (response.status === 401 || response.status === 403) {
    throw new Error(`Unauthorized: ${response.status}`);
  }

  if (isSuccess(response)) {
    return response.json();
  }

  throw new Error(`Failed to get dynamic page: ${response.status}`);
}

/**
 * Fetches the navigation config for a category from the __nav__ config record.
 * Always returns { tabs: [{ name, tabLabel }], pageTitle: string }.
 * tabs is empty ([]) when useTabs is false; pageTitle is "" when not configured.
 */
export async function getCategoryNavMetadata(category) {
  const params = new URLSearchParams({ category });
  const response = await apiFetch(apiUrl(`/dynamicPages/navConfig?${params}`));

  if (response.status === 401 || response.status === 403) {
    throw new Error(`Unauthorized: ${response.status}`);
  }

  if (!response.ok) {
    throw new Error(`Failed to get category nav config: ${response.status}`);
  }

  const config = await response.json();
  // Always expose pageTitle so SubheaderV2 can show it even for non-tabbed categories.
  // tabs is empty when useTabs is false so the nav is hidden but the title still shows.
  const tabs =
    config?.useTabs && Array.isArray(config.tabs)
      ? config.tabs.map((tab) => ({
          name: tab.name,
          tabLabel: tab.tabLabel,
          // External-link tabs carry a URL and no `name`; pass it through so the
          // subheader renders an anchor instead of a broken `/undefined` route.
          ...(tab.externalUrl ? { externalUrl: tab.externalUrl } : {}),
        }))
      : [];

  return { tabs, pageTitle: config?.pageTitle || "" };
}

// Specific util to build url when given basePath instead of full category/name/version context
export function getManagedImageUrl(
  basePath,
  imageValue,
  { defaultExtension = "" } = {},
) {
  const filename = getImageFilename(imageValue);
  if (!filename) {
    return undefined;
  }

  if (/^https?:\/\//i.test(filename)) {
    return filename;
  }

  // Shared assets resolve from the page root: drop a trailing version segment
  // from basePath (e.g. .../<name>/<version>/ -> .../<name>/) and append the ref.
  if (isSharedAssetRef(filename)) {
    const pageRootBase = basePath.replace(/(?<![^/])[^/]+\/?$/, "");
    return `${pageRootBase}${filename}`;
  }

  const hasExtension = /\.[a-z0-9]+$/i.test(filename);
  const finalFilename =
    !hasExtension && defaultExtension
      ? `${filename}${defaultExtension}`
      : filename;

  return `${basePath}${finalFilename}`;
}
